import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import AutoComplete, { AutoCompleteOption } from "../common/AutoComplete";
import GenericFilterBox from "../common/GenericFilterBox";
import { nameof } from "../common/ObjectUtils";
import AssociationTable from "./AssociationTable";
import EquipmentAssociationService, {
  EquipmentAssociationRequests,
} from "./EquipmentAssociationService";
import { EquipmentAssociationTypes } from "./Types";
import AssociationKitsTable from "./AssociationKitsTable";
import { getAllAcriss, getAllKits } from "./Utils";
import useToggle from "../../hooks/useToggle";
import Dialogs from "./dialogs";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import { ContextOperation } from "../../context/OperationContext";
import { useHasScope } from "../../../../config/globalConfig";

const EquipmentAssociationPage = () => {
  const hasScope = useHasScope;
  const { tenant } = useContext(ContextOperation);
  const [listAcriss, setListAcriss] = useState<AutoCompleteOption[]>([]);
  const [listKits, setListKits] = useState<AutoCompleteOption[]>([]);

  const [selectedAcriss, setSelectedAcriss] = useState<
    EquipmentAssociationTypes.ACRISS | undefined
  >(undefined);

  const [showCreateAcriss, toggleCreateAcriss] = useToggle();
  const [showManageAcrissKits, toggleManageAcrissKits] = useToggle();

  const t = useOperationTranslation();

  useEffect(() => {
    getAllAcriss(tenant).then((response) => setListAcriss(response));
    getAllKits(tenant).then((response) => setListKits(response));
  }, [tenant]);

  const formik = useFormik<EquipmentAssociationRequests.GetAll>({
    initialValues: {},
    onSubmit: () => {},
  });

  const [datasourceAcriss, totalElementsAcriss, updateDatasourceAcriss] =
    useAgGridCustomDatasource(
      EquipmentAssociationService.get.filterAcriss,
      [tenant, formik.values],
      [tenant, formik.submitCount]
    );

  const [datasourceKit, totalElementsKit, updateDatasourceKit] =
    useAgGridCustomDatasource(
      EquipmentAssociationService.get.selectedKits,
      [tenant, selectedAcriss?.id],
      [tenant, selectedAcriss?.id]
    );

  return (
    <Mui.Box paddingBottom="16px">
      <form id="equipment-association-form" onSubmit={formik.handleSubmit}>
        <GenericFilterBox
          formId="equipment-association-form"
          onReset={formik.resetForm}
        >
          <AutoComplete
            options={listAcriss}
            label={t("vehicleEquipmentAssociation.filter.acriss")}
            value={formik.values.acrissId || ""}
            onChange={(value) =>
              formik.setFieldValue(
                nameof<EquipmentAssociationRequests.GetAll>("acrissId"),
                value
              )
            }
            fullWidth
          />

          <AutoComplete
            options={listKits}
            label={t("vehicleEquipmentAssociation.filter.kit")}
            value={formik.values.equipmentKitId || ""}
            onChange={(value) =>
              formik.setFieldValue(
                nameof<EquipmentAssociationRequests.GetAll>("equipmentKitId"),
                value
              )
            }
            fullWidth
          />
        </GenericFilterBox>
      </form>

      <Mui.Grid container spacing="16px" padding="0px 16px">
        <Mui.Grid item md={8} xs={12}>
          <Mui.Card>
            <Mui.CardHeader
              action={
                <Mui.Box display="flex" justifyContent="end">
                  {hasScope("SCOPE_CREATE_MDVEHICLE_EQUIPMENTKIT") && (
                    <Mui.Tooltip
                      title={t(
                        "vehicleEquipmentAssociation.tables.acriss.actions.add"
                      )}
                    >
                      <Mui.IconButton
                        color="accent"
                        onClick={toggleCreateAcriss}
                      >
                        <MuiIcons.Add />
                      </Mui.IconButton>
                    </Mui.Tooltip>
                  )}
                </Mui.Box>
              }
              title={
                <Mui.Typography variant="body">
                  {t("vehicleEquipmentAssociation.tables.acriss.title")}
                </Mui.Typography>
              }
            />

            <AssociationTable
              tenant={tenant}
              table={{
                totalElements: totalElementsAcriss,
                datasource: datasourceAcriss,
                onSelect: (val: EquipmentAssociationTypes.ACRISS) =>
                  setSelectedAcriss(val),
                selectedAcriss: selectedAcriss,
              }}
              updateDatasourceAcriss={updateDatasourceAcriss}
              updateDatasourceKit={updateDatasourceKit}
              submitCount={formik.submitCount}
            />
          </Mui.Card>
        </Mui.Grid>

        <Mui.Grid item md={4} xs={12}>
          <Mui.Card>
            <Mui.CardHeader
              action={
                <Mui.Box display="flex" justifyContent="end">
                  {hasScope("SCOPE_UPDATE_MDVEHICLE_EQUIPMENTKIT") && (
                    <Mui.Tooltip
                      title={t(
                        "vehicleEquipmentAssociation.tables.equipmentKits.actions.edit"
                      )}
                    >
                      <Mui.IconButton
                        color="accent"
                        onClick={toggleManageAcrissKits}
                        disabled={selectedAcriss ? false : true}
                      >
                        <MuiIcons.Edit />
                      </Mui.IconButton>
                    </Mui.Tooltip>
                  )}
                </Mui.Box>
              }
              title={
                <Mui.Typography variant="body">
                  {t("vehicleEquipmentAssociation.tables.equipmentKits.title")}
                </Mui.Typography>
              }
            />

            <AssociationKitsTable
              tenant={tenant}
              table={{
                datasource: datasourceKit,
                totalElements: totalElementsKit,
              }}
              selectedAcriss={selectedAcriss}
              updateDatasourceAcriss={updateDatasourceAcriss}
              updateDatasourceKit={updateDatasourceKit}
            />
          </Mui.Card>
        </Mui.Grid>
      </Mui.Grid>

      <Dialogs.CreateEquipmentKitAssociation
        tenant={tenant}
        open={showCreateAcriss}
        onClose={toggleCreateAcriss}
        updateDatasourceAcriss={updateDatasourceAcriss}
        updateDatasourceKit={updateDatasourceKit}
      />

      <Dialogs.ManageEquipmentKits
        tenant={tenant}
        open={showManageAcrissKits}
        onClose={toggleManageAcrissKits}
        acris={selectedAcriss}
        updateDatasourceAcriss={updateDatasourceAcriss}
        updateDatasourceKit={updateDatasourceKit}
      />
    </Mui.Box>
  );
};

export default EquipmentAssociationPage;
